import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { buildMetadata } from "@/lib/metadata";
import { cn } from "@/lib/utils";
import { UploadService } from "@/services/upload-service";
import { useMutation } from "@tanstack/react-query";
import { Link, createFileRoute, useRouter } from "@tanstack/react-router";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  CloudUpload,
  FileSpreadsheet,
  Trash2,
} from "lucide-react";
import { useId, useRef, useState } from "react";
import { toast } from "sonner";

type UploadListItem = {
  id: string;
  file: File;
};

const createUploadId = () => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `upload-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const Route = createFileRoute("/_main/orders/upload")({
  component: RouteComponent,
  head: () => ({
    meta: buildMetadata({
      title: "Upload Orders • Openfarming",
      description:
        "Upload CSV, Excel, PDF, or image files to quickly add purchase orders.",
      keywords: ["orders", "upload", "csv", "excel"],
    }),
  }),
});

function RouteComponent() {
  const inputId = useId();
  const dropRef = useRef<HTMLLabelElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<UploadListItem[]>([]);
  const [isDragActive, setIsDragActive] = useState(false);
  const router = useRouter();

  const handleFileSelection = (filesList: FileList | null) => {
    if (!filesList || filesList.length === 0) {
      return;
    }

    setUploadedFiles((prev) => {
      const existingSignatures = new Set(
        prev.map(
          ({ file }) => `${file.name}-${file.size}-${file.lastModified}`,
        ),
      );
      const nextItems = Array.from(filesList)
        .filter((file) => {
          if (file.size <= 0) {
            return false;
          }
          const signature = `${file.name}-${file.size}-${file.lastModified}`;
          if (existingSignatures.has(signature)) {
            return false;
          }
          existingSignatures.add(signature);
          return true;
        })
        .map((file) => ({
          id: createUploadId(),
          file,
        }));

      if (nextItems.length === 0) {
        return prev;
      }
      return [...prev, ...nextItems];
    });
  };

  const formatFileSize = (size: number) => {
    if (size <= 0) {
      return "0 KB";
    }
    const units = ["B", "KB", "MB", "GB"];
    const exponent = Math.min(
      Math.floor(Math.log(size) / Math.log(1024)),
      units.length - 1,
    );
    const formatted = size / 1024 ** exponent;
    return `${formatted.toFixed(exponent === 0 ? 0 : 1)} ${units[exponent]}`;
  };

  const handleRemoveFile = (id: string) => {
    setUploadedFiles((prev) => prev.filter((file) => file.id !== id));
  };

  const uploadMutation = useMutation({
    mutationFn: (files: File[]) => UploadService.createUploadRequest(files),
    onSuccess: (data) => {
      toast.success("Upload request created", {
        description: "We’ll process your files and notify you shortly.",
      });
      setUploadedFiles([]);
      router.navigate({
        to: "/orders/upload-summary/$requestId",
        params: { requestId: data.id },
      });
    },
    onError: (error) => {
      const description =
        error instanceof Error
          ? error.message
          : "Unable to upload files right now.";
      toast.error("Upload failed", { description });
    },
  });

  const hasFiles = uploadedFiles.length > 0;

  const handleSubmit = () => {
    if (!hasFiles || uploadMutation.isPending) {
      return;
    }
    const files = uploadedFiles.map((item) => item.file);
    uploadMutation.mutate(files);
  };

  const stepIndicators = [
    { id: "upload", label: "Upload", active: true },
    { id: "summary", label: "Summary", active: false },
  ];

  return (
    <div className="flex w-full flex-col gap-8 p-6">
      <Link
        to="/orders"
        className="inline-flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Orders
      </Link>

      <header className="space-y-2">
        <div>
          <h1 className="font-semibold text-3xl">Upload Orders</h1>
        </div>
      </header>

      <section className="w-full">
        <ol className="flex flex-wrap items-center gap-4 text-sm">
          {stepIndicators.map((step, index) => (
            <li key={step.id} className="flex items-center gap-3">
              <span
                className={cn(
                  "flex size-9 items-center justify-center rounded-full border font-semibold text-sm",
                  step.active
                    ? "border-brand-darkGreen bg-brand-yellowGreen text-brand-darkGreen"
                    : "border-muted text-muted-foreground",
                )}
              >
                {String(index + 1).padStart(2, "0")}
              </span>
              <span
                className={cn(
                  "font-medium",
                  step.active ? "text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
              {index < stepIndicators.length - 1 && (
                <ChevronRight className="size-4 text-muted-foreground" />
              )}
            </li>
          ))}
        </ol>

        <Card
          className="w-full gap-0"
          style={{ border: "0px", boxShadow: "none" }}
        >
          <CardHeader className="px-6">
            <CardTitle className="text-lg">
              Upload supporting documents
            </CardTitle>
          </CardHeader>
          <CardContent className="px-6">
            <label
              ref={dropRef}
              htmlFor={inputId}
              onDragEnter={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDragOver={(event) => {
                event.preventDefault();
                setIsDragActive(true);
              }}
              onDragLeave={(event) => {
                if (
                  dropRef.current &&
                  !dropRef.current.contains(event.relatedTarget as Node)
                ) {
                  setIsDragActive(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                setIsDragActive(false);
                handleFileSelection(event.dataTransfer.files);
              }}
              className={cn(
                "mt-6 flex min-h-[280px] cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed bg-muted/40 text-center transition-colors",
                isDragActive
                  ? "border-brand-darkGreen bg-brand-yellowGreen/10"
                  : "border-muted-foreground/40",
              )}
            >
              <div className="flex size-20 items-center justify-center rounded-full bg-white shadow-sm">
                <CloudUpload className="size-8 text-muted-foreground" />
              </div>
              <p className="mt-6 font-semibold">
                Click to upload or drag and drop
              </p>
              <p className="text-muted-foreground text-sm">
                CSV, Excel (.xlsx, .xls), PDF, images (max 5MB)
              </p>
              <input
                id={inputId}
                type="file"
                className="sr-only"
                accept=".csv,.xlsx,.xls,application/pdf,image/*"
                multiple
                onChange={(event) => {
                  handleFileSelection(event.target.files);
                  event.target.value = "";
                }}
              />
            </label>
          </CardContent>

          <Separator className="mx-6 mt-10" />

          <CardContent className="space-y-4 px-6 py-6">
            <div className="flex items-center justify-between">
              <p className="font-medium">
                Uploaded Files ({uploadedFiles.length})
              </p>
              <span className="text-muted-foreground text-sm">
                Max size per file: 5MB
              </span>
            </div>

            {!hasFiles ? (
              <div className="flex flex-col items-center gap-2 rounded-xl border border-muted-foreground/30 border-dashed py-12 text-center text-muted-foreground text-sm">
                <Check className="size-6 text-brand-darkGreen" />
                Your files will appear here after uploading.
              </div>
            ) : (
              <div className="space-y-3">
                {uploadedFiles.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 rounded-2xl border border-muted bg-white px-4 py-3 shadow-sm"
                  >
                    <span className="rounded-full bg-brand-yellowGreen/60 p-2 text-brand-darkGreen">
                      <FileSpreadsheet className="size-5" />
                    </span>
                    <div className="flex flex-1 flex-col">
                      <span className="font-medium">{item.file.name}</span>
                      <span className="text-muted-foreground text-sm">
                        {formatFileSize(item.file.size)}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveFile(item.id)}
                      className="text-muted-foreground hover:text-destructive"
                      aria-label={`Remove ${item.file.name}`}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>

          <CardFooter className="justify-end gap-4 px-6 py-6">
            <Button
              asChild
              variant="ghost"
              className="text-muted-foreground hover:text-foreground"
            >
              <Link to="/orders">Cancel</Link>
            </Button>
            <Button
              type="button"
              onClick={handleSubmit}
              disabled={!hasFiles || uploadMutation.isPending}
              className="min-w-[220px] rounded-xl bg-foreground text-background hover:bg-foreground/90 disabled:opacity-75"
            >
              {uploadMutation.isPending
                ? "Uploading..."
                : "Continue to Summary"}
            </Button>
          </CardFooter>
        </Card>
      </section>
    </div>
  );
}
